import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import CurrencySwitcher from '@/components/feature/CurrencySwitcher';
import { avatarOr } from '@/lib/defaultImages';

const NAV_LINKS = [
  { tkey: 'home.nav_features', label: 'Fonctionnalités', id: 'features' },
  { tkey: 'home.nav_how', label: 'Comment ça marche', id: 'how-it-works' },
  { tkey: 'home.nav_pricing', label: 'Tarifs', id: 'pricing' },
  { tkey: 'home.nav_faq', label: 'FAQ', id: 'faq' },
];

const RESOURCE_LINKS = [
  { label: 'Campagnes créateurs', to: '/missions-campagnes', icon: 'ri-megaphone-line' },
  { label: 'Blog', to: '/blog', icon: 'ri-article-line' },
  { label: 'Documentation', to: '/documentation', icon: 'ri-book-open-line' },
  { label: 'API', to: '/api', icon: 'ri-terminal-line' },
  { label: 'Communauté', to: '/communaute', icon: 'ri-team-line' },
  { label: 'Status', to: '/status', icon: 'ri-pulse-line' },
];

interface NavbarProps {
  forceScrolled?: boolean;
}

export default function Navbar({ forceScrolled = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(forceScrolled);
  const [loginDropdownOpen, setLoginDropdownOpen] = useState(false);
  const loginDropdownRef = useRef<HTMLDivElement>(null);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const resourcesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const { t } = useTranslation();
  const brandName = brand.name;
  const brandLogo = brand.logo;

  const isScrolled = forceScrolled || scrolled;

  const navItems = NAV_LINKS;

  // Ferme les menus déroulants au clic extérieur
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (loginDropdownRef.current && !loginDropdownRef.current.contains(e.target as Node)) {
        setLoginDropdownOpen(false);
      }
      if (resourcesRef.current && !resourcesRef.current.contains(e.target as Node)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (forceScrolled) return;
    const handleScroll = (event?: Event) => {
      const target = event?.target;
      const containerTop = target instanceof HTMLElement && target !== document.body && target !== document.documentElement
        ? target.scrollTop
        : 0;
      const top = Math.max(
        window.scrollY || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0,
        containerTop,
      );
      setScrolled(top > 24);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Capture les conteneurs qui scrollent en interne (layout mobile).
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', handleScroll);
    };
  }, [forceScrolled]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const textColor = isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white';
  const iconColor = isScrolled ? 'text-foreground-600' : 'text-white/80';

  return (
    <header className="fixed top-0 left-0 w-full z-50">
      {/* Fond : sur petit écran la barre garde toujours un voile sombre pour
          rester lisible, même si l'image du hero n'est pas encore chargée. */}
      {isScrolled ? (
        <div className="absolute inset-0 bg-background-50/95 backdrop-blur-md border-b border-background-200/70"></div>
      ) : (
        <>
          <div className="absolute inset-0 bg-foreground-950/55 backdrop-blur-sm md:hidden"></div>
          <div className="pointer-events-none absolute inset-0 hidden md:block bg-gradient-to-b from-foreground-950/60 via-foreground-950/25 to-transparent"></div>
        </>
      )}
      <div className="relative w-full px-3 md:px-6 lg:px-10">
        <div className="flex items-center gap-2 md:gap-4 h-14 md:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 no-underline shrink-0">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors overflow-hidden ${isScrolled ? 'bg-primary-500' : 'bg-white/12'}`}>
              {brandLogo ? (
                <img src={brandLogo} alt={brandName || 'ZIFEK'} className="w-full h-full object-contain p-0.5" />
              ) : (
                <span className={`text-lg font-bold font-heading transition-colors ${isScrolled ? 'text-background-50' : 'text-white/85'}`}>
                  {(brandName || 'Z').charAt(0)}
                </span>
              )}
            </div>
            <span className={`text-lg md:text-xl font-bold font-heading transition-colors ${isScrolled ? 'text-foreground-950' : 'text-white'}`}>
              {brandName || 'ZIFEK'}
            </span>
          </Link>

          {/* Navigation principale — TOUJOURS visible, à toutes les largeurs.
              Sur petit écran la rangée défile horizontalement : les liens
              (Fonctionnalités, Tarifs, FAQ…) restent accessibles, sans icône
              hamburger. */}
          <nav
            className="flex-1 min-w-0 flex items-center gap-0.5 lg:gap-1 overflow-x-auto no-scrollbar justify-center"
            aria-label="Navigation principale"
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`px-2.5 lg:px-4 py-2 rounded-full text-xs lg:text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${textColor}`}
              >
                {t(item.tkey, { defaultValue: item.label })}
              </button>
            ))}

            <div className="relative" ref={resourcesRef}>
              <button
                onClick={() => setResourcesOpen((v) => !v)}
                className={`flex items-center gap-1 px-2.5 lg:px-4 py-2 rounded-full text-xs lg:text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${textColor}`}
              >
                Ressources
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${resourcesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {resourcesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 rounded-xl border border-background-200/70 bg-background-50 p-1.5 shadow-lg">
                  {RESOURCE_LINKS.map((r) => (
                    <Link
                      key={r.to}
                      to={r.to}
                      onClick={() => setResourcesOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors no-underline"
                    >
                      <i className={`${r.icon} text-primary-500`}></i>
                      {r.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Actions de droite */}
          <div className="flex items-center gap-1.5 md:gap-3 shrink-0">
            {user ? (
              <div className="flex items-center gap-2 md:gap-3">
                <Link
                  to="/mon-compte"
                  className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer bg-accent-500/15 text-accent-600 hover:bg-accent-500/25 no-underline"
                >
                  <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden">
                    <img src={avatarOr(user.image)} alt="" className="w-full h-full object-cover" />
                  </div>
                  <span className="hidden md:inline max-w-[100px] truncate">{user.name || user.user_name}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${iconColor} hover:bg-white/10`}
                  aria-label="Déconnexion"
                >
                  <i className="ri-logout-box-r-line text-lg"></i>
                </button>
              </div>
            ) : (
              <>
                {/* Menu Connexion : disponible à toutes les largeurs (icône seule sur mobile) */}
                <div className="relative" ref={loginDropdownRef}>
                  <button
                    onClick={() => setLoginDropdownOpen(!loginDropdownOpen)}
                    className={`flex items-center gap-1.5 px-2.5 md:px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      loginDropdownOpen
                        ? (isScrolled ? 'bg-background-100 text-foreground-950' : 'bg-white/10 text-white/90')
                        : textColor
                    }`}
                  >
                    <i className="ri-user-line text-sm"></i>
                    <span className="hidden md:inline">{t('auth.login', { defaultValue: 'Connexion' })}</span>
                    <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${loginDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {loginDropdownOpen && (
                    <div className={`absolute top-full right-0 mt-2 w-56 rounded-xl border overflow-hidden animate-fade-in z-50 ${
                      isScrolled
                        ? 'bg-background-50 border-background-200/70 shadow-lg'
                        : 'bg-foreground-950/60 backdrop-blur-xl border-white/10'
                    }`}>
                      <div className="p-1.5">
                        <button
                          onClick={() => { navigate('/login-client'); setLoginDropdownOpen(false); }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer text-left ${
                            isScrolled
                              ? 'text-foreground-700 hover:bg-accent-50 hover:text-accent-700'
                              : 'text-white/80 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isScrolled ? 'bg-accent-100' : 'bg-white/10'
                          }`}>
                            <i className={`ri-user-smile-line text-sm ${isScrolled ? 'text-accent-600' : 'text-accent-300'}`}></i>
                          </div>
                          <div>
                            <div className="text-sm font-semibold">{t('auth.client_space', { defaultValue: 'Espace client' })}</div>
                            <div className={`text-xs font-normal ${isScrolled ? 'text-foreground-400' : 'text-white/40'}`}>{t('auth.client_space_desc', { defaultValue: 'Mes achats, mes fichiers' })}</div>
                          </div>
                        </button>
                        <button
                          onClick={() => { navigate('/login'); setLoginDropdownOpen(false); }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer text-left ${
                            isScrolled
                              ? 'text-foreground-700 hover:bg-primary-50 hover:text-primary-700'
                              : 'text-white/80 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isScrolled ? 'bg-primary-100' : 'bg-white/10'
                          }`}>
                            <i className={`ri-store-2-line text-sm ${isScrolled ? 'text-primary-600' : 'text-primary-300'}`}></i>
                          </div>
                          <div>
                            <div className="text-sm font-semibold">{t('auth.pro_space', { defaultValue: 'Espace professionnel' })}</div>
                            <div className={`text-xs font-normal ${isScrolled ? 'text-foreground-400' : 'text-white/40'}`}>{t('auth.pro_space_desc', { defaultValue: 'Gérer ma boutique' })}</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => navigate('/register')}
                  className={`hidden lg:inline-flex px-4 lg:px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer items-center gap-2 ${
                    isScrolled
                      ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                      : 'bg-white/12 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20 hover:border-white/30'
                  }`}
                >
                  {t('home.create_site', { defaultValue: 'Créer mon site' })}
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </>
            )}

            <LanguageSwitcher compact dark={!isScrolled} className="hidden md:flex" />
            <CurrencySwitcher compact dark={!isScrolled} nativeCurrency="MAD" className="hidden md:flex" />
          </div>
        </div>
      </div>
    </header>
  );
}